import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar, { type FilterState } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';
import VideoCard from '@/components/feature/VideoCard';
import VideoGridSkeleton from '@/components/feature/VideoGridSkeleton';
import EmptyState from '@/components/feature/EmptyState';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { filterVideos, paginate } from '@/utils/filter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = categories.find((c) => c.id === categoryId);
  const allVideos = videos as Video[];

  const [filters, setFilters] = useState<FilterState>({ region: 'all', duration: 'all', time: 'all' });
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useDocumentTitle({
    title: category ? `${category.name} - 成人快手` : '分类 - 成人快手',
    description: category
      ? `成人快手${category.name}分类，${category.desc}，涵盖丰富的成人主题短视频内容。`
      : '成人快手视频分类浏览。',
    keywords: category ? `成人快手,${category.name},成人短视频,${category.name}视频` : '成人快手,分类,短视频',
  });

  useEffect(() => {
    setFilters({ region: 'all', duration: 'all', time: 'all' });
    setSort('latest');
    setPage(1);
  }, [categoryId]);

  const filtered = useMemo(
    () => filterVideos(allVideos, { category: categoryId, ...filters, sort }),
    [allVideos, categoryId, filters, sort],
  );

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 320);
    return () => clearTimeout(t);
  }, [categoryId, filters, sort, page]);

  const { items, totalPages } = paginate(filtered, page, PAGE_SIZE);
  const activeCount = [filters.region, filters.duration, filters.time].filter(
    (v) => v !== 'all',
  ).length;

  if (!category) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="分类不存在"
          description="你访问的分类可能已被移除，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '分类' }, { label: category.name }]} />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-secondary-500/15 text-secondary-300">
          <i className={`${category.icon} text-2xl`} />
        </span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {category.name}视频
          </h1>
          <p className="mt-1 text-sm text-foreground-500">{category.desc}</p>
        </div>
      </div>

      <div className="mt-6">
        <FilterBar
          value={filters}
          onChange={setFilters}
          onReset={() => setFilters({ region: 'all', duration: 'all', time: 'all' })}
          activeCount={activeCount}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个视频
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="flex rounded-full border border-background-300/60 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort('latest')}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                sort === 'latest' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              最新
            </button>
            <button
              type="button"
              onClick={() => setSort('hot')}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                sort === 'hot' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              最热
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <VideoGridSkeleton count={PAGE_SIZE} />
        ) : items.length === 0 ? (
          <EmptyState
            icon="ri-filter-off-line"
            title="暂无符合条件的视频"
            description="试试调整或重置筛选条件，发现更多内容。"
            actionLabel="重置筛选"
            onAction={() => setFilters({ region: 'all', duration: 'all', time: 'all' })}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}
      </div>

      {!loading && items.length > 0 && (
        <Pagination current={page} total={totalPages} onChange={setPage} />
      )}
    </div>
  );
}